import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { SelectChangeEvent } from "@mui/material";
import BackendPaginationTable from "common/Table/TableWithPagination";
import Loader from "layout/Loader";
import { useGetReviewsService } from "subModule/src/services/client/useClient";
import ReviewSearchHeader from "./ReviewSearchHeader";
import { useTranslation } from "react-i18next";
interface SearchFormData {
  [key: string]: string | any;
}
interface ReviewProps {
  userId?: number;
  accountId?: number;
  clientId?: number;
}
export default function ReviewsListing(props: ReviewProps) {
  const { t } = useTranslation();
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["rating", "dateInterval"]);
  const [search, setSearch] = useState<any>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    rating: "",
    startDate: null,
    endDate: null,
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetReviewsService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    ...(props?.accountId && { accountId: props.accountId }),
    ...(props?.userId && { userId: props.userId }),
    ...(props?.clientId && { clientId: props.clientId }),
  });
  const RatingList = [
    { label: t("all"), value: "" },
    { label: 1 + " " + t("star"), value: "1" },
    { label: 2 + " " + t("stars"), value: "2" },
    { label: 3 + " " + t("stars"), value: "3" },
    { label: 4 + " " + t("stars"), value: "4" },
    { label: 5 + " " + t("stars"), value: "5" },
  ];
  const { enqueueSnackbar } = useSnackbar();
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.startDate, searchFormData.endDate]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.rating]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const clearSearch = () => {
    setFilter(["rating", "dateInterval"]);
    setSearchFormData({ rating: "", startDate: null, endDate: null });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) => {
      const selectedFilters =
        typeof value === "string" ? value.split(",") : value;
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      );
      if (selectedFilters.includes("dateInterval")) {
        updatedFormData.startDate = prevFormData.startDate;
        updatedFormData.endDate = prevFormData.endDate;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = field === "rating" ? (value ? value.value : "") : value;
    if (field === "rating" && searchFormData.rating === val) return;
    if (field === "dateInterval") {
      setSearchFormData((prevData) => ({
        ...prevData,
        startDate: value[0],
        endDate: value[1],
      }));
      if ((value[0] && value[1]) || (value[0] === null && value[1] === null)) {
        setLoading(true);
      }
    } else {
      setSearchFormData((prevData) => ({
        ...prevData,
        [field]: val,
      }));
    }
    if (field === "rating") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    const { startDate, endDate, ...rest } = searchFormData;
    if (startDate && endDate) {
      filteredSearch.startDate = startDate;
      filteredSearch.endDate = endDate;
    } else {
      // ❗ If one or both missing → remove both
      setSearchFormData((prev) => ({
        ...prev,
        startDate: null,
        endDate: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = value?.trim();
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  return (
    <Box>
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "rating", label: "rating", numeric: false },
            { id: "comment", label: "comment", numeric: false },
            { id: "customerName", label: "customer", numeric: false },
            { id: "vehicleName", label: "vehicle", numeric: false },
            { id: "vehiclePlateNumber", label: "plateNumber", numeric: false },
            { id: "createdAt", label: "createdAt", numeric: false },
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          // actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          rowClick={true}
          loadingSearch={false}
          headerComponent={
            <>
              <ReviewSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                {...{ filter, search, RatingList }}
              />
            </>
          }
        />
      )}
    </Box>
  );
}
